import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
} from '@angular/core';
import { WizDndManager } from './../../../services/dragndrop/wiz-dnd.manager';
import {
  WizDraggableComponent,
  WizDraggableItem,
  WizDropControlFn,
  WizDropItemEvent,
  WIZ_DRAGGABLE_COMPONENT,
} from './wiz-drag-drop.model';
import { WizDOMUtils } from '@wizeoo/akm-core-lib';

/**
 * Directive de gestion du drag and drop sur un container HTML.
 * - Le composant associé doit provider l'injectionToken WIZ_DRAGGABLE_COMPONENT.
 * - Le composant doit également gérer le déclenchement du drag/drop via l'appel de la méthode startDrag.
 */
@Directive({
  selector: '[wizDragDrop]',
})
export class WizDragDropContainer implements OnDestroy {
  // Drag autorisé
  @Input()
  public wizDragEnabled: boolean = true;

  private _wizDropEnabled: boolean = true;

  // Drop autorisé
  @Input()
  public set wizDropEnabled(pValue: boolean) {
    this._wizDropEnabled = pValue;

    // enregistrement dans le manager (ou pas)
    if (pValue) {
      WizDndManager.registerDropTarget(this);
    } else {
      WizDndManager.unRegisterDropTarget(this);
    }
  }

  // Suppression de l'élément déplacé en fin de drag/drop
  @Input()
  wizDragMove: boolean = false;

  /** Fonction de contrôle sur dragOver param = item return true si drop autorisé */
  @Input()
  wizDropControlFunction: WizDropControlFn;

  // Drag/drop interne pour reorder des éléments du container
  @Input()
  wizEnableSelfReorder: boolean = false;

  // Affichage de la position de l'élément droppé
  @Input()
  wizShowDropIndicator: boolean = false;

  get elementRef(): ElementRef {
    return this._elementRef;
  }

  get draggableContainer(): WizDraggableComponent {
    return this._draggableContainer;
  }

  private _mainContainer: HTMLElement;

  private _dropTargetElt: HTMLDivElement;

  constructor(
    private _elementRef: ElementRef,
    @Inject(DOCUMENT) private _document: Document,
    @Optional()
    @Inject(forwardRef(() => WIZ_DRAGGABLE_COMPONENT))
    private _draggableContainer?: WizDraggableComponent
  ) {}

  /**
   * Initiation d'un drag/drop sur un élément HTML
   *
   * @param pItem item qui subit le drag/drop (élément HTML + data métier optionnelle)
   */
  startDrag(pItem: WizDraggableItem) {
    if (this.wizDragEnabled) {
      WizDndManager.startDragAction(pItem, this);
    }
  }

  /**
   * Fonction de check au drag over, pour autoriser ou non le drop.
   */
  onDragOver(pEvent: WizDropItemEvent, pIsSelfDrop: boolean): boolean {
    // Self move disabled
    if (pIsSelfDrop && !this.wizEnableSelfReorder) {
      return false;
    }

    // Affichage de l'indicateur de drop
    if (this.wizShowDropIndicator) {
      if (!this._dropTargetElt) {
        this._dropTargetElt = this._createDropTargetElement();
      }
      WizDOMUtils.insertChildElement(
        this._dropTargetElt,
        this._mainContainer,
        pEvent.targetPosition
      );
    }

    if (
      (this._draggableContainer &&
        !this._draggableContainer.checkDragOver(pEvent)) ||
      (this.wizDropControlFunction && !this.wizDropControlFunction(pEvent))
    ) {
      return false;
    }
    return this._wizDropEnabled;
  }

  /** Fin de l'action de drag/drop. */
  onDragEnd(): void {
    if (this._dropTargetElt?.parentElement) {
      this._dropTargetElt?.parentElement.removeChild(this._dropTargetElt);
    }
  }

  /** 
   * Les fonctions onDragEnter et onDragLeave permettent de supprimer l'élément qui marque la position 
   * du drop (ligne pointillée) lorsqu'on quitte le composant. Sans ces fonctions, l'indicateur de drop
   * reste constamment affiché, même lorsqu'on survole un autre composant ou lorsqu'on arrête le drag&drop.
   * 
   * La solution, qui consiste à supprimer systématiquement cet élément sur l'événement dragleave, fonctionne
   * mais donne un effet de clignotement trés désagréable et pas précis. Pourquoi ? parce que le composant 
   * reçoit beaucoup trop d'événements dragenter et dragleave. 
   * Il faut supprimer l'indicateur de drop que lorsqu'on reçoit l'événement dragleave du composant parent uniquement.
   * 
   * Pour cela, l'astuce consiste à stocker la derniére cible de l'événement dragenter et de comparer cette
   * derniére cible à la cible de l'événement dragleave. Si les 2 sont égales, alors on sait qu'on vient de
   * quitter le composant parent.
   * 
   * @See https://stackoverflow.com/a/12270694
   * @See https://stackoverflow.com/a/26459269
   */

  // Derniére cible de l'événement dragenter
  private _lastDragEnterTarget:any;

  // Sur l'évenement dragenter, on conserve la cible
  onDragEnter(pEvent:WizDropItemEvent): void {
    this._lastDragEnterTarget = pEvent.event.target;
  }

  // Si la cible de l'événement dragleave est la même que la derniére cible de l'événement
  // dragenter, alors cela signifie qu'on est sorti du composant parent. On supprime donc l'élement
  // dropTargetElt, qui marque la position du drop et qui n'a plus lieu d'être affiché
  onDragLeave(pEvent:WizDropItemEvent): void {
    if (this._lastDragEnterTarget === pEvent.event.target && this._dropTargetElt?.parentElement) {
      this._dropTargetElt?.parentElement.removeChild(this._dropTargetElt);
    }
  }

  onDrop(pEvent: WizDropItemEvent, pIsSource: boolean) {
    if (pIsSource && this.wizDragMove) {
      this._draggableContainer?.onRemoveItemAfterDrag(pEvent.item);
    }
    if (!pIsSource) {
      this._draggableContainer?.onAddItemAfterDrag(pEvent);
    }
  }

  onSelfDrop(pEvent: WizDropItemEvent) {
    this._draggableContainer?.onMoveItemAfterDrag({
      ...pEvent,
      targetPosition: pEvent.targetPosition,
    });
  }

  getDropPosition(pEvent: DragEvent): number {
    if (!this._mainContainer) {
      this._mainContainer = this._getDragDropMainContainer();
    }
    const dropPosition: number = WizDOMUtils.determineItemPosition(
      pEvent.target as HTMLElement,
      this._mainContainer
    );
    // dropPosition à -1 => on ajoute à la fin par défaut.
    return (
      (dropPosition > -1
        ? dropPosition
        : this._mainContainer.childElementCount) -
      (this.wizShowDropIndicator && dropPosition !== 0 ? 1 : 0)
    );
  }

  /** Récupération du container HTML principal de drag/drop */
  private _getDragDropMainContainer(pElement?: HTMLElement): HTMLElement {
    const container = !!pElement ? pElement : this._elementRef.nativeElement;
    return container.tagName === 'DIV'
      ? container
      : this._getDragDropMainContainer(container.firstChild as HTMLElement);
  }

  /** Création de l'indicateur visuel de drop */
  private _createDropTargetElement(): HTMLDivElement {
    const divElement: HTMLDivElement = this._document.createElement('div');
    divElement.classList.add('wiz-drop-target');
    return divElement;
  }

  ngOnDestroy(): void {
    WizDndManager.unRegisterDropTarget(this);
  }
}
